// Safely converts values to DateTime objects
import { DateTime } from "luxon";

/**
 * Safely converts a value to a Luxon DateTime ISO string
 * @param value The value to convert (string, Date, or DateTime)
 * @param defaultValue Optional default value to return if conversion fails (defaults to current date)
 * @returns A valid ISO string
 */
export const safeToDateTime = (value: any, defaultValue?: DateTime): string => {
  try {
    if (!value) {
      return (defaultValue || DateTime.now()).toISO() || DateTime.now().toISO() || '';
    }
    
    if (value instanceof DateTime) {
      return value.toISO() || DateTime.now().toISO() || '';
    }
    
    if (value instanceof Date) {
      return DateTime.fromJSDate(value).toISO() || DateTime.now().toISO() || '';
    }
    
    // Assume it's a string and try to parse it
    return DateTime.fromISO(value).toISO() || DateTime.now().toISO() || '';
  } catch (e) {
    console.warn(`Failed to convert value to DateTime: ${value}`, e);
    return (defaultValue || DateTime.now()).toISO() || DateTime.now().toISO() || '';
  }
};

/**
 * Safely creates a JavaScript Date object from various inputs
 * @param value The value to convert (string, Date, or DateTime)
 * @returns A valid JavaScript Date object or null if conversion fails
 */
export const safeToJSDate = (value: any): Date | null => {
  try {
    if (!value) {
      return new Date();
    }
    
    if (value instanceof Date) {
      return value;
    }
    
    if (value instanceof DateTime) {
      return value.toJSDate();
    }
    
    // Try parsing as ISO string
    const dt = DateTime.fromISO(value);
    if (dt.isValid) {
      return dt.toJSDate();
    }
    
    // Last attempt - direct JS Date parsing
    return new Date(value);
  } catch (e) {
    console.warn(`Failed to convert value to JS Date: ${value}`, e);
    return new Date();
  }
};

/**
 * Parses a date string in dd/MM/yyyy HH:mm format to DateTime ISO string
 * Useful for parsing user-proposed appointment dates from form data
 * @param dateString String in format "dd/MM/yyyy HH:mm" (e.g., "13/06/2025 14:30")
 * @returns A valid ISO string or null if parsing fails
 */
export const parseUserProposedDate = (dateString: string): string | null => {
  try {
    if (!dateString || typeof dateString !== 'string') return null;
    
    // Handle format: "dd/MM/yyyy HH:mm" (e.g., "13/06/2025 14:30")
    const match = dateString.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})\s+(\d{1,2}):(\d{1,2})$/);
    if (!match) return null;
    
    const [, day, month, year, hour, minute] = match;
    const parsedDate = DateTime.fromObject({
      day: parseInt(day, 10),
      month: parseInt(month, 10),
      year: parseInt(year, 10),
      hour: parseInt(hour, 10),
      minute: parseInt(minute, 10),
    });
    
    if (!parsedDate.isValid) {
      console.warn('Invalid date parsed from:', dateString);
      return null;
    }
    
    return parsedDate.toISO() || null;
  } catch (e) {
    console.warn('Failed to parse user proposed date:', dateString, e);
    return null;
  }
};
